/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useReducer } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { When } from "react-if";
import { Box, Button, FlexContainer, theme, Spacer } from "@nordcloud/gnui";
import { Sidebar } from "~/components";
import { useDisclosure, useQueryState } from "~/hooks";
import { useGetCloudResourceFilterValues } from "../../hooks";
import { ResourceQuery } from "../../types/ResourceQuery";
import { AllocationFilters } from "./AllocationFilters";
import { AttributesFilters } from "./AttributesFilters";
import { ResourceStatusFilters } from "./ResourceStatusFilters";
import { State } from "./types";

type SidebarFooterProps = {
  onApply: () => void;
  onClear: () => void;
};

function SidebarFooter({ onApply, onClear }: SidebarFooterProps) {
  return (
    <Col>
      <Row>
        <Box spacing="spacing02" css={{ marginBottom: 0 }}>
          <FlexContainer justifyContent="start" alignItems="center">
            <Button
              mr={theme.spacing.spacing04}
              severity="low"
              onClick={onApply}
            >
              Apply
            </Button>
            <Button
              mr={theme.spacing.spacing04}
              severity="low"
              onClick={onClear}
            >
              Clear
            </Button>
          </FlexContainer>
        </Box>
      </Row>
    </Col>
  );
}

// eslint-disable-next-line complexity
function buildState(queryState: ResourceQuery) {
  return {
    // Allocation
    applicationsAndEnvironments: [],
    excludeApplicationsAndEnvironments: false,
    unallocatedEstateRecords: [],
    excludeUnallocatedEstateRecords: false,
    // Attributes
    operatingSystems: [],
    excludeOperatingSystems: false,
    cloudProviders: queryState.cloudProviders ?? [],
    excludeCloudProviders: queryState.excludeCloudProviders ?? false,
    regions: queryState.regions ?? [],
    excludeRegions: queryState.excludeRegions ?? false,
    cloudAccounts: queryState.cloudAccounts ?? [],
    excludeCloudAccounts: queryState.excludeCloudAccounts ?? false,
    cloudServices: queryState.cloudServices ?? [],
    excludeCloudServices: queryState.excludeCloudServices ?? false,
    resourceCategories: [],
    excludeResourceCategories: false,
    resourceTypes: queryState.resourceTypes ?? [],
    excludeResourceTypes: queryState.excludeResourceTypes ?? false,
    resourceTagNamesAndTagValues: queryState.resourceTagNamesAndTagValues ?? [],
    excludeResourceTagNamesAndTagValues:
      queryState.excludeResourceTagNamesAndTagValues ?? false,
    resourceMetadataKeysAndValues:
      queryState.resourceMetadataKeysAndValues ?? [],
    excludeResourceMetadataKeysAndValues:
      queryState.excludeResourceMetadataKeysAndValues ?? false,
    discoveryTimeRange: {
      startDateUTC: queryState.discoveryTimeStartUTC,
      endDateUTC: queryState.discoveryTimeEndUTC,
    },
    excludeDiscoveryTimeRange: false,
    resourceLifeSpanRange: {
      from: queryState.resourceLifeSpanFrom,
      to: queryState.resourceLifeSpanTo,
    },
    excludeResourceLifeSpanRange: queryState.excludeResourceLifeSpan ?? false,
    resourceGroups: [],
    excludeResourceGroups: false,
    plans: [],
    excludePlans: false,
    // Resource Status
    lastPatchingStatuses: queryState.lastPatchingStatuses ?? [],
    excludeLastPatchingStatuses:
      queryState.excludeLastPatchingStatuses ?? false,
  };
}

export function ResourceFiltersSidebar({
  resourceType,
}: {
  resourceType: string;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();

  const { data: filtersData } = useGetCloudResourceFilterValues();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const clearAllFilters = () => {
    updateState({
      // Allocation
      applicationsAndEnvironments: [],
      excludeApplicationsAndEnvironments: false,
      unallocatedEstateRecords: [],
      excludeUnallocatedEstateRecords: false,
      // Attributes
      operatingSystems: [],
      excludeOperatingSystems: false,
      cloudProviders: [],
      excludeCloudProviders: false,
      regions: [],
      excludeRegions: false,
      cloudAccounts: [],
      excludeCloudAccounts: false,
      cloudServices: [],
      excludeCloudServices: false,
      resourceCategories: [],
      excludeResourceCategories: false,
      resourceTypes: [],
      excludeResourceTypes: false,
      resourceTagNamesAndTagValues: [],
      excludeResourceTagNamesAndTagValues: false,
      resourceMetadataKeysAndValues: [],
      excludeResourceMetadataKeysAndValues: false,
      discoveryTimeRange: {
        startDateUTC: undefined,
        endDateUTC: undefined,
      },
      excludeDiscoveryTimeRange: false,
      resourceLifeSpanRange: {
        from: undefined,
        to: undefined,
      },
      excludeResourceLifeSpanRange: false,
      resourceGroups: [],
      excludeResourceGroups: false,
      plans: [],
      excludePlans: false,
      // Resource Status
      lastPatchingStatuses: [],
      excludeLastPatchingStatuses: false,
    });

    updateQueryState({
      ...queryState,
      cloudServices: [],
      excludeCloudServices: false,
      allocation: undefined,
      regions: [],
      excludeRegions: false,
      resourceTypes: [],
      excludeResourceTypes: false,
      cloudProviders: [],
      excludeCloudProviders: false,
      cloudAccounts: [],
      excludeCloudAccounts: false,
      discoveryTimeStartUTC: undefined,
      discoveryTimeEndUTC: undefined,
      excludeDiscoveryTimeRange: false,
      resourceMetadataKeysAndValues: [],
      excludeResourceMetadataKeysAndValues: false,
      resourceTagNamesAndTagValues: [],
      excludeResourceTagNamesAndTagValues: false,
      resourceLifeSpanFrom: undefined,
      resourceLifeSpanTo: undefined,
      excludeResourceLifeSpan: false,
      lastPatchingStatuses: [],
      excludeLastPatchingStatuses: false,
      // reset page
      page: "1",
    });

    close();
  };

  const applyFilters = () => {
    updateQueryState({
      ...queryState,
      cloudServices: state.cloudServices,
      excludeCloudServices: state.excludeCloudServices,
      regions: state.regions,
      excludeRegions: state.excludeRegions,
      resourceTypes: state.resourceTypes,
      excludeResourceTypes: state.excludeResourceTypes,
      cloudProviders: state.cloudProviders,
      excludeCloudProviders: state.excludeCloudProviders,
      cloudAccounts: state.cloudAccounts,
      excludeCloudAccounts: state.excludeCloudAccounts,
      discoveryTimeStartUTC: state.discoveryTimeRange.startDateUTC,
      discoveryTimeEndUTC: state.discoveryTimeRange.endDateUTC,
      excludeDiscoveryTimeRange: state.excludeDiscoveryTimeRange,
      resourceMetadataKeysAndValues: state.resourceMetadataKeysAndValues,
      excludeResourceMetadataKeysAndValues:
        state.excludeResourceMetadataKeysAndValues,
      resourceTagNamesAndTagValues: state.resourceTagNamesAndTagValues,
      excludeResourceTagNamesAndTagValues:
        state.excludeResourceTagNamesAndTagValues,
      resourceLifeSpanFrom: state.resourceLifeSpanRange.from,
      resourceLifeSpanTo: state.resourceLifeSpanRange.to,
      lastPatchingStatuses: state.lastPatchingStatuses,
      excludeLastPatchingStatuses: state.excludeLastPatchingStatuses,
      // reset page
      page: "1",
    });

    close();
  };

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <SidebarFooter onApply={applyFilters} onClear={clearAllFilters} />
          }
        >
          <AllocationFilters state={state} updateState={updateState} />
          <Spacer margin={"0 0 1rem 0"} />
          <AttributesFilters
            state={state}
            updateState={updateState}
            filtersData={filtersData}
            resourceType={resourceType}
          />
          <Spacer margin={"0 0 1rem 0"} />
          <ResourceStatusFilters state={state} updateState={updateState} />
        </Sidebar>
      </When>
    </>
  );
}
